import {
  Box,
  Button,
  Chip,
  Link as MuiLink,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { DataGrid, type GridColDef, type GridPaginationModel } from '@mui/x-data-grid';
import { useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router';

import type { AppError } from '../../api/contract';
import { useAuth } from '../../auth/AuthProvider';
import type { DataSource } from '../../config/capabilities';
import { useRepository } from '../../data/RepositoryProvider';
import { useResource } from '../../hooks/useResource';
import { paths } from '../../routes/paths';
import { DataSourceBadge } from '../../shared/data/DataSourceBadge';
import { PageState } from '../../shared/data/PageState';
import { ListFilterPanel } from '../../shared/filter/ListFilterPanel';
import { PageHeader } from '../../shared/layout/PageHeader';
import {
  defaultWithdrawalPolicyQuery,
  defaultWithdrawalVelocityQuery,
  withdrawalControlsRepositoryToken,
} from './repository';
import { formatControlMoney } from './types';
import type {
  PartnerWithdrawalPolicy,
  PartnerWithdrawalVelocityRule,
  WithdrawalControlStatus,
  WithdrawalVelocityDimension,
} from './types';
import { WithdrawalPolicyDialog } from './WithdrawalPolicyDialog';

type PolicyDialogState =
  | { readonly mode: 'create'; readonly policy?: undefined }
  | { readonly mode: 'edit' | 'status'; readonly policy: PartnerWithdrawalPolicy };

export function WithdrawalControlsPanel({ dataSource }: { readonly dataSource: DataSource }) {
  const { partnerId = '' } = useParams();
  const auth = useAuth();
  const repository = useRepository(withdrawalControlsRepositoryToken);
  const { t } = useTranslation();
  const [refreshKey, setRefreshKey] = useState(0);
  const [policyQuery, setPolicyQuery] = useState(defaultWithdrawalPolicyQuery);
  const [velocityQuery, setVelocityQuery] = useState(defaultWithdrawalVelocityQuery);
  const [policyDialog, setPolicyDialog] = useState<PolicyDialogState | null>(null);
  const policies = useResource(
    (signal) => repository.listPolicies(partnerId, policyQuery, signal),
    [repository, partnerId, policyQuery, refreshKey],
    () => false,
  );
  const velocityRules = useResource(
    (signal) => repository.listVelocityRules(partnerId, velocityQuery, signal),
    [repository, partnerId, velocityQuery, refreshKey],
    () => false,
  );
  const canManage =
    auth.status === 'authenticated' &&
    auth.principal?.permissions.includes('WITHDRAWAL_POLICY_MANAGE') === true;
  const refresh = () => setRefreshKey((current) => current + 1);

  const policyColumns = useMemo<GridColDef<PartnerWithdrawalPolicy>[]>(
    () => [
      { field: 'currencyCode', headerName: t('withdrawalControls.fields.currency'), width: 100 },
      {
        field: 'limits',
        headerName: t('withdrawalControls.fields.requestLimits'),
        sortable: false,
        minWidth: 250,
        flex: 1,
        valueGetter: (_value, row) =>
          `${formatControlMoney(row.minimumAmount, row.currencyCode)} \u2013 ${formatControlMoney(row.maximumAmount, row.currencyCode)}`,
      },
      {
        field: 'status',
        headerName: t('withdrawalControls.fields.status'),
        width: 125,
        renderCell: ({ value }) => <StatusChip status={value as WithdrawalControlStatus} />,
      },
      {
        field: 'actions',
        headerName: t('withdrawalControls.fields.actions'),
        sortable: false,
        filterable: false,
        width: 230,
        renderCell: ({ row }) =>
          canManage ? (
            <Stack direction="row" spacing={0.5}>
              <Button onClick={() => setPolicyDialog({ mode: 'edit', policy: row })} size="small">
                {t('actions.edit')}
              </Button>
              <Button
                color={row.status === 'ACTIVE' ? 'warning' : 'primary'}
                onClick={() => setPolicyDialog({ mode: 'status', policy: row })}
                size="small"
              >
                {row.status === 'ACTIVE'
                  ? t('withdrawalControls.actions.suspendPolicy')
                  : t('withdrawalControls.actions.activatePolicy')}
              </Button>
            </Stack>
          ) : null,
      },
    ],
    [canManage, t],
  );
  const velocityColumns = useMemo<GridColDef<PartnerWithdrawalVelocityRule>[]>(
    () => [
      { field: 'currencyCode', headerName: t('withdrawalControls.fields.currency'), width: 100 },
      { field: 'dimension', headerName: t('withdrawalControls.fields.dimension'), width: 135 },
      {
        field: 'windowSeconds',
        headerName: t('withdrawalControls.fields.windowSeconds'),
        width: 145,
      },
      {
        field: 'maximumCount',
        headerName: t('withdrawalControls.fields.maximumCount'),
        width: 145,
        valueGetter: (value) => value ?? '\u2014',
      },
      {
        field: 'maximumAmount',
        headerName: t('withdrawalControls.fields.maximumAmount'),
        minWidth: 180,
        valueGetter: (value, row) =>
          typeof value === 'string' ? formatControlMoney(value, row.currencyCode) : '\u2014',
      },
      {
        field: 'status',
        headerName: t('withdrawalControls.fields.status'),
        width: 125,
        renderCell: ({ value }) => <StatusChip status={value as WithdrawalControlStatus} />,
      },
    ],
    [t],
  );

  const status =
    policies.status === 'error' || velocityRules.status === 'error'
      ? 'error'
      : policies.status === 'loading' || velocityRules.status === 'loading'
        ? 'loading'
        : 'success';
  const cause = policies.error ?? velocityRules.error;

  return (
    <Box sx={{ minWidth: 0 }}>
      <PageHeader
        actions={<DataSourceBadge source={dataSource} />}
        subtitle={t('withdrawalControls.subtitle', { partnerId })}
        title={t('withdrawalControls.title')}
      />
      <MuiLink component={Link} to={`${paths.partners}/${encodeURIComponent(partnerId)}`}>
        {t('withdrawalControls.backToPartner')}
      </MuiLink>
      <Box sx={{ mt: 2 }}>
        <PageState error={safePageError(cause, t)} onRetry={refresh} status={status}>
          {policies.data === null || velocityRules.data === null ? null : (
            <Stack spacing={3}>
              <Paper component="section" variant="outlined" sx={{ p: 2, minWidth: 0 }}>
                <SectionHeader
                  action={
                    canManage ? (
                      <Button
                        onClick={() => setPolicyDialog({ mode: 'create' })}
                        variant="contained"
                      >
                        {t('withdrawalControls.actions.createPolicy')}
                      </Button>
                    ) : null
                  }
                  subtitle={t('withdrawalControls.policy.listNote')}
                  title={t('withdrawalControls.policy.title')}
                />
                <PolicyFilters
                  currency={policyQuery.filters.currencyCode}
                  onCurrency={(currencyCode) =>
                    setPolicyQuery((current) => ({
                      ...current,
                      page: 1,
                      filters: { ...current.filters, currencyCode },
                    }))
                  }
                  onStatus={(filterStatus) =>
                    setPolicyQuery((current) => ({
                      ...current,
                      page: 1,
                      filters: { ...current.filters, status: filterStatus },
                    }))
                  }
                  status={policyQuery.filters.status}
                />
                <Box
                  aria-label={t('withdrawalControls.policy.gridLabel')}
                  role="region"
                  sx={{ overflowX: 'auto', width: '100%' }}
                >
                  <Box sx={{ minWidth: 950 }}>
                    <DataGrid
                      columns={policyColumns}
                      disableColumnSorting
                      disableRowSelectionOnClick
                      onPaginationModelChange={(model) =>
                        setPolicyQuery((current) => fromGridPage(current, model))
                      }
                      pageSizeOptions={[25, 50, 100]}
                      paginationMode="server"
                      paginationModel={{
                        page: policyQuery.page - 1,
                        pageSize: policyQuery.pageSize,
                      }}
                      rowCount={policies.data.total}
                      rows={policies.data.items}
                    />
                  </Box>
                </Box>
              </Paper>
              <Paper component="section" variant="outlined" sx={{ p: 2, minWidth: 0 }}>
                <SectionHeader
                  action={null}
                  subtitle={t('withdrawalControls.velocity.listNote')}
                  title={t('withdrawalControls.velocity.title')}
                />
                <VelocityFilters
                  currency={velocityQuery.filters.currencyCode}
                  dimension={velocityQuery.filters.dimension}
                  onCurrency={(currencyCode) =>
                    setVelocityQuery((current) => ({
                      ...current,
                      page: 1,
                      filters: { ...current.filters, currencyCode },
                    }))
                  }
                  onDimension={(dimension) =>
                    setVelocityQuery((current) => ({
                      ...current,
                      page: 1,
                      filters: { ...current.filters, dimension },
                    }))
                  }
                  onStatus={(filterStatus) =>
                    setVelocityQuery((current) => ({
                      ...current,
                      page: 1,
                      filters: { ...current.filters, status: filterStatus },
                    }))
                  }
                  status={velocityQuery.filters.status}
                />
                <Box
                  aria-label={t('withdrawalControls.velocity.gridLabel')}
                  role="region"
                  sx={{ overflowX: 'auto', width: '100%' }}
                >
                  <Box sx={{ minWidth: 1050 }}>
                    <DataGrid
                      columns={velocityColumns}
                      disableColumnSorting
                      disableRowSelectionOnClick
                      onPaginationModelChange={(model) =>
                        setVelocityQuery((current) => fromGridPage(current, model))
                      }
                      pageSizeOptions={[25, 50, 100]}
                      paginationMode="server"
                      paginationModel={{
                        page: velocityQuery.page - 1,
                        pageSize: velocityQuery.pageSize,
                      }}
                      rowCount={velocityRules.data.total}
                      rows={velocityRules.data.items}
                    />
                  </Box>
                </Box>
              </Paper>
            </Stack>
          )}
        </PageState>
      </Box>
      {policyDialog === null ? null : (
        <WithdrawalPolicyDialog
          mode={policyDialog.mode}
          onClose={() => setPolicyDialog(null)}
          onSaved={refresh}
          open
          partnerId={partnerId}
          policy={policyDialog.policy}
        />
      )}
    </Box>
  );
}

function SectionHeader({
  action,
  subtitle,
  title,
}: {
  readonly action: ReactNode;
  readonly subtitle: string;
  readonly title: string;
}) {
  return (
    <Stack
      direction={{ xs: 'column', sm: 'row' }}
      spacing={1}
      sx={{ justifyContent: 'space-between', mb: 2 }}
    >
      <div>
        <Typography component="h2" variant="h5">
          {title}
        </Typography>
        <Typography color="text.secondary" variant="body2">
          {subtitle}
        </Typography>
      </div>
      {action}
    </Stack>
  );
}

function PolicyFilters({
  currency,
  onCurrency,
  onStatus,
  status,
}: {
  readonly currency: string;
  readonly onCurrency: (value: string) => void;
  readonly onStatus: (value: WithdrawalControlStatus | '') => void;
  readonly status: WithdrawalControlStatus | '';
}) {
  return (
    <FilterPanel titleKey="withdrawalControls.policy.title">
      <PolicyFilterFields
        currency={currency}
        onCurrency={onCurrency}
        onStatus={onStatus}
        status={status}
      />
    </FilterPanel>
  );
}

function VelocityFilters({
  currency,
  dimension,
  onCurrency,
  onDimension,
  onStatus,
  status,
}: {
  readonly currency: string;
  readonly dimension: WithdrawalVelocityDimension | '';
  readonly onCurrency: (value: string) => void;
  readonly onDimension: (value: WithdrawalVelocityDimension | '') => void;
  readonly onStatus: (value: WithdrawalControlStatus | '') => void;
  readonly status: WithdrawalControlStatus | '';
}) {
  const { t } = useTranslation();
  return (
    <FilterPanel titleKey="withdrawalControls.velocity.title">
      <PolicyFilterFields
        currency={currency}
        onCurrency={onCurrency}
        onStatus={onStatus}
        status={status}
      />
      <TextField
        label={t('withdrawalControls.fields.dimensionFilter')}
        onChange={(event) => onDimension(event.target.value as WithdrawalVelocityDimension | '')}
        select
        size="small"
        value={dimension}
      >
        <MenuItem value="">{t('withdrawalControls.filters.all')}</MenuItem>
        {(['PARTNER', 'MEMBER', 'CREDENTIAL', 'IP'] as const).map((item) => (
          <MenuItem key={item} value={item}>
            {item}
          </MenuItem>
        ))}
      </TextField>
    </FilterPanel>
  );
}

function FilterPanel({
  children,
  titleKey,
}: {
  readonly children: ReactNode;
  readonly titleKey: string;
}) {
  const { t } = useTranslation();
  return (
    <ListFilterPanel
      ariaLabel={t('filterPanel.label', { page: t(titleKey) })}
      primaryFields={children}
    />
  );
}

interface PolicyFilterFieldsProps {
  readonly currency: string;
  readonly onCurrency: (value: string) => void;
  readonly onStatus: (value: WithdrawalControlStatus | '') => void;
  readonly status: WithdrawalControlStatus | '';
}

function PolicyFilterFields({ currency, onCurrency, onStatus, status }: PolicyFilterFieldsProps) {
  const { t } = useTranslation();
  return (
    <>
      <TextField
        label={t('withdrawalControls.fields.currencyFilter')}
        onChange={(event) => onCurrency(event.target.value)}
        size="small"
        value={currency}
      />
      <TextField
        label={t('withdrawalControls.fields.statusFilter')}
        onChange={(event) => onStatus(event.target.value as WithdrawalControlStatus | '')}
        select
        size="small"
        value={status}
      >
        <MenuItem value="">{t('withdrawalControls.filters.all')}</MenuItem>
        <MenuItem value="ACTIVE">ACTIVE</MenuItem>
        <MenuItem value="SUSPENDED">SUSPENDED</MenuItem>
      </TextField>
    </>
  );
}

function StatusChip({ status }: { readonly status: WithdrawalControlStatus }) {
  return <Chip color={status === 'ACTIVE' ? 'success' : 'warning'} label={status} size="small" />;
}

function fromGridPage<Query extends { readonly page: number; readonly pageSize: number }>(
  query: Query,
  model: GridPaginationModel,
): Query {
  return { ...query, page: model.page + 1, pageSize: model.pageSize };
}

function safePageError(cause: unknown, t: (key: string) => string) {
  const error = isAppError(cause) ? cause : null;
  return { message: t('withdrawalControls.errors.load'), requestId: error?.requestId ?? null };
}

function isAppError(value: unknown): value is AppError {
  return (
    typeof value === 'object' &&
    value !== null &&
    typeof (value as Partial<AppError>).code === 'string' &&
    'requestId' in value
  );
}
